import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { Reveal } from "../components/Reveal";
import { BadgeIcon, SubBadge, DonorBadge } from "../lib/badges";
import { StaffName } from "../components/StaffName";
import { Flame, ArrowLeft, UserX, Gamepad2 } from "lucide-react";

const ROLE_COLORS = { lonestar: "#00A3FF", valkyra: "#FF3B30", manticore: "#34C759" };

export default function Operatore() {
  const { login } = useParams();
  const { t } = useLang();
  const [member, setMember] = useState(undefined);
  const [steam, setSteam] = useState(undefined);

  useEffect(() => {
    setMember(undefined);
    setSteam(undefined);
    api.get(`/members/${login}`).then(({ data }) => {
      setMember(data.member);
      setSteam(data.steam || null);
    }).catch(() => setMember(null));
  }, [login]);

  if (member === undefined) {
    return <main className="pt-32 pb-28 min-h-screen" data-testid="member-loading" />;
  }

  if (member === null) {
    return (
      <main className="pt-32 pb-28">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <UserX className="w-10 h-10 text-valkyra mx-auto mb-6" />
          <h1 className="font-display text-5xl uppercase mb-4" data-testid="member-not-found">{t("member.notfound")}</h1>
          <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.2em] text-wardogs hover:underline" data-testid="member-back-link">
            {t("member.back")}
          </Link>
        </div>
      </main>
    );
  }

  const roleColor = ROLE_COLORS[member.role] || "#8A8D91";
  const since = member.created_at ? new Date(member.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : null;

  return (
    <main className="pt-32 pb-28" data-testid="member-page">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <Reveal>
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-steel hover:text-wardogs transition-colors duration-300 mb-10" data-testid="member-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> {t("member.back")}
          </Link>
          <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-10 relative overflow-hidden">
            {member.role && <div className="absolute top-0 left-0 right-0 h-px" style={{ background: roleColor }} />}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-8">
              {member.avatar && <img src={member.avatar} alt="" className="w-24 h-24 chamfer border border-[#1A1A1A]" data-testid="member-avatar" />}
              <div className="flex-1">
                <p className="hud-label mb-2">// OPERATORE</p>
                {member.is_staff ? (
                  <h1 className="font-display text-5xl uppercase leading-none mb-3" data-testid="member-name">
                    <StaffName name={member.display_name} iconClass="w-8 h-8" />
                  </h1>
                ) : (
                  <h1 className="font-display text-5xl uppercase leading-none mb-3" style={{ color: roleColor }} data-testid="member-name">
                    {member.display_name}
                  </h1>
                )}
                <div className="flex flex-wrap items-center gap-3">
                  {member.role && (
                    <span className="chamfer-sm border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ borderColor: `${roleColor}66`, color: roleColor, background: `${roleColor}14` }} data-testid="member-role">
                      {member.role}
                    </span>
                  )}
                  {member.subscribed && <SubBadge tier={member.sub_tier} testid="member-sub-badge" />}
                  {member.donated && <DonorBadge testid="member-donor-badge" />}
                  <span className="flex items-center gap-1.5 font-mono text-[11px] text-steel" data-testid="member-visits">
                    <BadgeIcon visits={member.visits} testid="member-badge" /> {member.visits} {t("profile.visits").toLowerCase()}
                  </span>
                  {(member.streak || 0) > 0 && (
                    <span className="flex items-center gap-1.5 font-mono text-[11px] text-steel" data-testid="member-streak">
                      <Flame className={`w-3.5 h-3.5 ${member.streak >= 3 ? "text-wardogs" : "text-[#2A2A2A]"}`} /> {member.streak}
                    </span>
                  )}
                </div>
                {since && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A4D51] mt-4" data-testid="member-since">{t("member.since", { date: since })}</p>}
              </div>
            </div>
          </div>
        </Reveal>

        {steam && (
          <Reveal delay={0.1}>
            <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-8 mt-6" data-testid="member-steam-card">
              <p className="hud-label mb-5 text-wardogs">// STATISTICHE STEAM</p>
              {steam.private ? (
                <p className="font-mono text-xs text-steel" data-testid="member-steam-private">Profilo Steam privato — le statistiche non sono pubbliche.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <div className="chamfer-sm border border-[#1A1A1A] bg-[#050505] p-4 text-center" data-testid="member-steam-hours">
                      <p className="font-display text-4xl text-wardogs">{steam.playtime_hours != null ? `${steam.playtime_hours}h` : "—"}</p>
                      <p className="hud-label mt-1">Ore su WARDOGS</p>
                    </div>
                    <div className="chamfer-sm border border-[#1A1A1A] bg-[#050505] p-4 text-center" data-testid="member-steam-achievements">
                      <p className="font-display text-4xl text-[#00A3FF]">{steam.achievements_unlocked != null ? `${steam.achievements_unlocked}${steam.achievements_total ? `/${steam.achievements_total}` : ""}` : "—"}</p>
                      <p className="hud-label mt-1">Obiettivi</p>
                    </div>
                    <div className="chamfer-sm border border-[#1A1A1A] bg-[#050505] p-4 text-center col-span-2 md:col-span-1" data-testid="member-steam-owns">
                      <p className={`font-display text-4xl ${steam.owns_wardogs ? "text-[#34C759]" : "text-[#2A2A2A]"}`}>{steam.owns_wardogs ? "SÌ" : steam.owns_wardogs === false ? "NO" : "—"}</p>
                      <p className="hud-label mt-1">Possiede il gioco</p>
                    </div>
                  </div>
                  {steam.profileurl && (
                    <a href={steam.profileurl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-steel hover:text-wardogs transition-colors duration-300" data-testid="member-steam-link">
                      <Gamepad2 className="w-3.5 h-3.5" /> Profilo Steam di {steam.personaname || member.display_name}
                    </a>
                  )}
                </>
              )}
            </div>
          </Reveal>
        )}
      </div>
    </main>
  );
}
